import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxcCostbasisV1TaxLotQueryServiceTaxLotGet } from '@interxfi/api-client/generated/hooks/ixc-costbasis-v1-tax-lot-query-service-tax-lot-get';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { buildBreadcrumbs } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcCostbasisV1TaxLotState } from '@interxfi/types';
import { CostBasisTaxLotEntityMenu } from '../../../../../../../../../../../features/cost-basis/tax-lot/cost-basis-tax-lot-entity-menu';
import { CostBasisTaxLotEventSummary } from '../../../../../../../../../../../features/cost-basis/tax-lot/cost-basis-tax-lot-event';
import { getCostBasisTaxLotStateListItems } from '../../../../../../../../../../../features/cost-basis/tax-lot/cost-basis-tax-lot-list-items';
import { getCostBasisTaxLotPath } from '../../../../../../../../../../../features/cost-basis/tax-lot/cost-basis-tax-lot-routing';
import { CostBasisTaxLotStatusPill } from '../../../../../../../../../../../features/cost-basis/tax-lot/cost-basis-tax-lot-status-pill';
import { CostBasisTaxLotSummaryBar } from '../../../../../../../../../../../features/cost-basis/tax-lot/cost-basis-tax-lot-summary-bar';
import { AccountBreadcrumb } from '../../../../../../../../../../../features/registration/account/account-breadcrumb';
import { RegistrationAccountPageHeader } from '../../../../../../../../../../../features/registration/account/registration-account-page-header';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_cost-basis/asset/$assetId/tax-lot/$taxLotId')({
  loader: ({ params }) =>
    buildBreadcrumbs([
      {
        id: `account-${params.accountId}-asset-${params.assetId}-tax-lot-list`,
        label: <AccountBreadcrumb accountId={params.accountId} displayIdOnly />,
        to: getCostBasisTaxLotPath({ kind: 'list', keys: { accountId: params.accountId, assetId: params.assetId } }),
      },
      {
        id: `account-${params.accountId}-asset-${params.assetId}-tax-lot-${params.taxLotId}`,
        label: 'Tax Lot',
        to: getCostBasisTaxLotPath({ kind: 'detail', keys: { accountId: params.accountId, assetId: params.assetId, taxLotId: params.taxLotId } }),
        suffix: <CopyButton content={params.taxLotId} label="Copy Tax Lot ID" tooltip={{ copy: params.taxLotId, copied: 'Copied Tax Lot ID' }} />,
      },
    ]),
  head: () => getPageHead('Tax Lot'),
  component: AccountAssetTaxLotDetail,
});

function getListItems(data: IxcCostbasisV1TaxLotState | undefined) {
  const items = getCostBasisTaxLotStateListItems(data);

  return {
    general: {
      left: [
        items.statusPill,
        items.data.totalAdjustedQuantity,
        items.data.totalAdjustedCost,
        items.data.openQuantity,
      ] as const satisfies ReadonlyListItem[],
      right: [items.taxLotId, items.data.holdingPeriodStartDate] as const satisfies ReadonlyListItem[],
    },
    openingTransaction: {
      left: [items.data.openingTransaction.quantity, items.data.openingTransaction.cost] as const satisfies ReadonlyListItem[],
      right: [
        items.data.openingTransaction.tradeTimestamp,
        items.data.openingTransaction.tradeDate,
        items.data.openingTransaction.settleDate,
        ...match(items.data.openingTransaction.transactionType)
          .with({ portfolioTransaction: P.not(P.nullish) }, ({ portfolioTransaction }) => [portfolioTransaction.value.transactionId])
          .with({ lotSplit: P.not(P.nullish) }, ({ lotSplit }) => [lotSplit.value.fromTaxLot])
          .with({ reorganization: P.not(P.nullish) }, ({ reorganization }) => [reorganization.value.fromTaxLot, reorganization.value.fromAssetId])
          .otherwise(() => []),
      ] as const satisfies ReadonlyListItem[],
    },
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load tax lot', 'view this tax lot');

function AccountAssetTaxLotDetail() {
  const { accountId, taxLotId } = Route.useParams();
  const request = { accountId, taxLotId } as const;
  const { data, error, isPending } = useIxcCostbasisV1TaxLotQueryServiceTaxLotGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.taxLot), [data?.taxLot]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <RegistrationAccountPageHeader
            accountId={accountId}
            actions={<CostBasisTaxLotEntityMenu accountId={accountId} taxLotId={taxLotId} />}
            pills={<CostBasisTaxLotStatusPill isPending={showPending} size="large" status={data?.taxLot?.status} />}
          />

          <CostBasisTaxLotSummaryBar {...request} />

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <CostBasisTaxLotEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>
      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Opening Transaction</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.openingTransaction.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.openingTransaction.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
